import cl from 'clsx';
import { ReactNode, useCallback, useEffect } from 'react';
import { useCopyToClipboard } from 'usehooks-ts';

import {
  telegramGetPeerTmeUrl,
  // telegramGetStartchannelLink,
} from '@synapex/shared2/telegram';

import CopyIcon from '@synapex/frontend-shared/icons/copy-16.svg?react';
import { isAbortError } from '@synapex/frontend-shared/utils';

import { useTmeInfoQuery } from '@/true-fsd/data/telegram';

import classes from './shared/AwaitNewChannel.module.scss';
import { Button } from '@/true-fsd/shared/ui/ButtonLink';
// import { awaitNewChannelCancel } from '@/true-fsd/data/advertiser-account/api-client';
import {
  AdvertiserAccount,
  Invite,
  Query,
  Workspace,
  Selector,
  NewAccountChannelRequest,
  ExistentAccountChannelRequest,
} from '@/true-fsd/data/advertiser-account';

import { BotPermissionsImage } from './shared/BotPermissionsImage';
import { NavigateFunction, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';

const BOT_LINK = telegramGetPeerTmeUrl({
  username: import.meta.env.VITE_BOT_USERNAME,
}).href;

/*

Doesn't work on Mac

const BOT_ADD_LINK = telegramGetStartchannelLink(
  import.meta.env.VITE_BOT_USERNAME,
);

const BotLinkParagraph = () => (
  <p>
    Воспользуйтесь этой ссылкой чтобы добавить бота с необходимыми разрешениями{' '}
    <CopyLink href={BOT_ADD_LINK}>{BOT_ADD_LINK}</CopyLink>
  </p>
);

*/

/*

type UserPollingArgs = {
  telegram_channel_id: string;
  onError?: (error: unknown) => void;
} & Pick<PollNewChannelProps, 'pollingCb'>;

const usePolling = ({
  telegram_channel_id,
  onError,
  pollingCb,
}: UserPollingArgs) => {
  useEffect(() => {
    const abortController = new AbortController();

    const interval = setInterval(() => {
      void pollingCb({ telegram_channel_id }, { abortController })
        .then((stop) => {
          if (stop) {
            clearInterval(interval);
          }
        })
        .catch((error) => {
          clearInterval(interval);
          if (!isAbortError(error) && onError) {
            onError(error);
          }
        });
    }, 1000);

    return () => {
      clearInterval(interval);
      abortController.abort();
    };
  }, [telegram_channel_id, onError, pollingCb]);
};
*/

type UseChannelRequestPollingArgs = {
  telegram_channel_id: string;
  onAccount: (r: { invite: Invite; createdAccount: AdvertiserAccount }) => void;
  onChannel: (r: Workspace) => void;
  onError: (r: unknown) => void;
};
const useChannelRequestPolling = ({
  telegram_channel_id,
  onAccount,
  onChannel,
  onError,
}: UseChannelRequestPollingArgs) => {
  const client = useQueryClient();

  useEffect(() => {
    const abortController = new AbortController();

    const interval = setInterval(() => {
      void Query.queryChannelRequest(
        client,
        { telegram_channel_id },
        abortController,
      )
        .then((result) => {
          if (result.status === 'ACCOUNT') {
            onAccount(
              Selector.createdAccountAndInvite(result.advertiserAccount),
            );
          } else if (result.status === 'CHANNEL') {
            onChannel(result.channel);
          }
        })
        .catch((error: unknown) => {
          if (!isAbortError(error)) {
            console.error(error);
            onError(error);
          }
        });
    }, 3000);

    return () => {
      clearInterval(interval);
      abortController.abort();
    };
  }, [client, telegram_channel_id, onAccount, onChannel, onError]);
};

type ChannelBadgeProps = {
  photo?: string | undefined | null;
  title?: string | undefined | null;
  username?: string | undefined | null;
  className?: string;
};
const ChannelBadge = ({
  className,
  photo,
  title,
  username,
}: ChannelBadgeProps) => {
  return (
    <div className={cl(classes.channelBadge, className)}>
      {photo && <img src={photo} className={classes.photo} />}
      <div className={classes.namesCol}>
        <div className={classes.title}>{title}</div>
        {username && <div className={classes.username}>{`@${username}`}</div>}
      </div>
    </div>
  );
};

type CopyLinkProps = {
  href: string;
  children: ReactNode;
};
const CopyLink = ({ children, href }: CopyLinkProps) => {
  const [, copyFn] = useCopyToClipboard();
  const onClick = () => copyFn(href);

  return (
    <span className={classes.copyLink}>
      <a href={href} target="_blank" rel="noreferrer" className={classes.link}>
        {children}
      </a>
      <button type="button" className={classes.button}>
        <CopyIcon onClick={onClick} />
      </button>
    </span>
  );
};

type RenderLayoutProps = {
  slotHeader: ReactNode;
  slotButtons: ReactNode;

  children: ReactNode;
};

const noOp = () => {
  //no op
};

export type PollNewChannelProps = {
  RenderLayout: (p: RenderLayoutProps) => ReactNode;

  channelRequest: NewAccountChannelRequest | ExistentAccountChannelRequest;

  onCancel: (navigate: NavigateFunction) => void;

  onAccount?: (
    navigate: NavigateFunction,
    r: { invite: Invite; createdAccount: AdvertiserAccount },
  ) => void;
  onChannel?: (navigate: NavigateFunction, r: Workspace) => void;
  onError?: (navigate: NavigateFunction, error: unknown) => void;
  onContinue?: (navigate: NavigateFunction) => void;
};
export const PollNewChannel = ({
  RenderLayout,
  channelRequest,
  onCancel,
  onAccount = noOp,
  onChannel = noOp,
  onError = noOp,
  onContinue,
}: PollNewChannelProps) => {
  const { data: tmeInfo } = useTmeInfoQuery({
    username: channelRequest.channel_username,
    invite_hash: channelRequest.telegram_channel_invite_hash,
  });
  // console.log({ data, channelInfo })
  const telegram_channel_id = channelRequest.telegram_channel_id;
  // usePolling({ telegram_channel_id, pollingCb });

  const navigate = useNavigate();

  useChannelRequestPolling({
    telegram_channel_id,
    onAccount: useCallback(
      (...args) => onAccount(navigate, ...args),
      [onAccount, navigate],
    ),
    onChannel: useCallback(
      (...args) => onChannel(navigate, ...args),
      [onChannel, navigate],
    ),
    onError: useCallback(
      (...args) => onError(navigate, ...args),
      [onError, navigate],
    ),
  });

  const cancel = Query.useDeleteChannelRequest({ telegram_channel_id });

  const onClickCancel = () => {
    //give some time to navigate out of page before its destruction
    setTimeout(cancel.mutate, 1000);
    onCancel(navigate);
  };

  return (
    <RenderLayout
      slotHeader={'Добавление бота'}
      slotButtons={
        <div className={classes.buttons}>
          <Button variant={'secondary'} onClick={onClickCancel}>
            Отмена
          </Button>
          {onContinue && (
            <Button variant={'primary'} onClick={() => onContinue(navigate)}>
              Продолжить
            </Button>
          )}
        </div>
      }
    >
      <div className={classes.formContent}>
        <ChannelBadge
          title={channelRequest.channel_name}
          username={channelRequest.channel_username}
          photo={
            tmeInfo?.photo ??
            tmeInfo?.smallPhoto ??
            channelRequest.small_photo_data_url
          }
        />
        <p>
          Добавьте бота{' '}
          <CopyLink
            href={BOT_LINK}
          >{`@${import.meta.env.VITE_BOT_USERNAME}`}</CopyLink>{' '}
          в Телеграм-канал или попросите сделать это администратора
          телеграм-канала.
        </p>

        <p>
          Не забудьте выдать необходимые доступы для бота, иначе сервис не
          сможет работать
        </p>

        <BotPermissionsImage />

        <p className={classes.bottom}>
          Как только бот будет добавлен в канал (или если он уже добавлен),
          целевая ссылка появится в течение 30 секунд
        </p>
      </div>
    </RenderLayout>
  );
};
